import React from 'react';

import Modal from 'antd/es/modal/Modal';
import type { Dayjs } from 'dayjs';
import { Field } from 'formik';
import * as Yup from 'yup';

import { ItemEditor } from '@common/react/components/Core/ItemEditor/ItemEditor';
import { useItemProviderContext } from '@common/react/components/Core/ItemProvider/ItemProvider';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';
import { FileInterface, FileType } from '@common/typescript/objects/FileInterface';
import { List } from '@common/typescript/objects/List';

import { rest } from '@app/components/Api';
import CustomAutoComplete from '@app/components/UI/Forms/CustomAutoComplete/CustomAutoComplete';
import CustomAutoTokenization from '@app/components/UI/Forms/CustomAutoTokenization/CustomAutoTokenization';
import CustomCheckbox from '@app/components/UI/Forms/CustomCheckbox/CustomCheckbox';
import CustomDatePicker from '@app/components/UI/Forms/CustomDatePicker/CustomDatePicker';
import FormikField from '@app/components/UI/Forms/CustomFormikField/CustomFormikField';
import CustomSelect from '@app/components/UI/Forms/CustomSelect/CustomSelect';
import FileUpload from '@app/components/UI/Forms/FileUpload/FileUpload';
import { GAeventSubmit } from '@app/utils/GAevents';
import { validateRequiredString } from '@app/utils/Validators';
import { formatListWithOr } from '@app/utils/formatListWithOr';
import { onFileUpload } from '@app/utils/onFileUpload';
import { transformStateList, transformVetList } from '@app/utils/transformList';
import AttachmentPreview from '@app/components/UI/Attachment/AttachmentPreview';
import CustomInput from '@app/components/UI/CustomInput/CustomInput';
import CustomReCAPTCHA from '@app/components/UI/CustomReCAPTCHA/CustomReCAPTCHA';
import PetMemorialPreviewModal from '@app/components/UI/PetMemorialModal/PetMemorialPreviewModal';
import candleImg from '@app/images/link-icons/candle.svg';
import { MemorialsRes, PetMemorialModalFormProps } from '@app/objects/PetMemorial';
import { ListOption } from '@app/objects/SidebarProps';
import { StatesListItem } from '@app/objects/StatesListItem';
import { Veterinarian } from '@app/objects/Veterinarian';
import '@app/scss/localCommon/components/attachment.scss';
import '@app/scss/components/petMemorialModalForm.scss';

const initialValues = {
	id: -1,
	petName: '',
	species: '',
	dateBirth: '',
	datePass: '',
	message: '',
	story: '',
	firstName: '',
	lastName: '',
	isDisplayed: true,
	city: '',
	state: '',
	email: '',
	isAssist: '',
	vetId: '',
	vetTes: '',
	share: '',
	filePath: [],
	captcha: '',
};

const validationSchema = Yup.object({
	firstName: validateRequiredString('First Name is a required field'),
	petName: validateRequiredString("Pet's Name is a required field"),
	dateBirth: validateRequiredString("Pet's Birthday is a required field"),
	datePass: validateRequiredString("Pet's Date of Passing is a required field"),
	message: validateRequiredString('Leave a message in memory of your pet is a required field'),
	story: validateRequiredString("Tell us the 'story' of your pet is a required field"),
	city: validateRequiredString('City is a required field'),
	state: validateRequiredString('State is a required field'),
	isAssist: validateRequiredString('Did Lap of Love assist you is a required field'),
	email: Yup.string().email('Email must be a valid Email').required('Email is a required field'),
	share: Yup.array().of(Yup.string().email('Please make sure all email addresses are entered correctly')),
	captcha: Yup.string().required('Please check the box to submit this form'),
});

const PetMemorialModalForm: React.FC<PetMemorialModalFormProps> = ({
	modalVisible,
	setModalVisible,
	sent,
	setSent,
}) => {
	const context = useItemProviderContext<MemorialsRes>();

	if (!context?.state) throw new Error('Error context');

	const { state: { loading } } = context;

	const species = [{ name: 'Dog' }, { name: 'Cat' }, { name: 'Other' }];

	const assistOptions = [
		{ value: 0, label: 'No' },
		{ value: 1, label: 'Yes' },
	];
	const toDate = (date: number) => {
		const dateObj = new Date(date);

		return dateObj.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
	};

	const [isChecked, setIsChecked] = React.useState(true);
	const [attachment, setAttachment] = React.useState<Array<FileInterface>>([]);
	const maxFile = 10000000;
	const imgExtension = ['jpg', 'jpeg', 'jfif', 'png'];
	const maxImgCount = 6;
	const [isConfirmed, setIsConfirmed] = React.useState<boolean>(false);

	const [modalPreviewVisible, setModalPreviewVisible] = React.useState<boolean>(false);
	const [formValues, setFormValues] = React.useState({
		...initialValues,
		datePost: toDate(+new Date()),
	});

	const [vetIdList, setVetIdList] = React.useState<Array<Veterinarian>>([]);
	const vetListOptions: Array<ListOption> = [];
	const [stateList, setStateList] = React.useState<Array<StatesListItem>>([]);
	const stateListOptions: Array<ListOption> = [];

	const openModal = (e, item) => {
		e.preventDefault();
		setFormValues((prevState) => ({
			...prevState,
			...item,
		}));
		setModalPreviewVisible(true);
	};

	const getVeterinarians = (name: string) => {
		rest.get<List<Veterinarian>>(`v1/veterinarians/?count=300&name=${name}`)
			.then((res) => {
				const vetIdListSorted = res.list.sort((a, b) => (a.firstName > b.firstName ? 1 : -1));
				setVetIdList(vetIdListSorted);
			});
	};
	transformVetList(vetIdList, vetListOptions);

	const toStrictDate: (value: Dayjs | null) => number | null = (value) => {
		return value ? new Date(value.toDate()).setUTCHours(0, 0, 0, 0) : null;
	};

	React.useEffect(() => {
		rest.get<List<StatesListItem>>('v1/states').then((res) => setStateList(res.list));
	}, []);

	transformStateList(stateList, stateListOptions);

	React.useEffect(() => {
		const imagesAttached: Array<string> = [];
		attachment.forEach((item) => {
			imagesAttached.push(item.src);
		});

		setFormValues((prevState) => ({
			...prevState,
			images: imagesAttached,
		}));
	}, [attachment]);

	React.useEffect(() => {
		setFormValues((prevState) => ({
			...prevState,
			isShowName: isChecked,
		}));
	}, [isChecked]);

	return (
		<>
			<Modal
				wrapClassName="pet-mem-modal pet-mem-modal_form modal-wrap"
				open={modalVisible}
				onOk={() => setModalVisible(false)}
				onCancel={() => setModalVisible(false)}
				width={1037}
				centered
			>
				<div className="pet-mem-modal__content pet-mem-modal_form">
					<div className="pet-mem-modal__container pet-mem-modal_form__container">
						<header className="pet-mem-modal_form__header">
							<div className="pet-mem-modal_form__header-candle">
								<img src={candleImg} alt="candle" />
							</div>

							{sent ? (
								<>
									<h2 className="pet-mem-modal_form__title pet-mem-modal_form__title_success">
										Thank you for submitting your memorial
									</h2>
									<h4 className="pet-mem-modal_form__subtitle ">
										We will post it within 5 business days and you will receive an email letting you know your angel's memorial is
										ready to view.
									</h4>
								</>
							) : (
								<>
									<h2 className="pet-mem-modal_form__title pet-mem-modal_form__title_desktop">Leave a memorial for a pet</h2>
									<h2 className="pet-mem-modal_form__title pet-mem-modal_form__title_mobile">Submit a memorial</h2>
									<h4 className="pet-mem-modal_form__subtitle pet-mem-modal_form__subtitle_desktop">
										Celebrate the life of your pet by sharing a story, detailing their life,
										<br />
										or just writing about the beautiful bond you shared with your beloved friend.
									</h4>
								</>
							)}
						</header>
						{!sent && (
							<div className="pet-mem-modal_form__form">
								<ItemEditor
									formikProps={{
										initialValues,
										validationSchema,
									}}
									readonly={false}
									withButtons={false}
									beforeSubmit={(values, action, submit) => {
										if (isConfirmed) {
											values.vetId = vetListOptions.find((option) => option.value === values.vetId)?.id || null;

											if (typeof values.vetId === 'number' || values.vetId === null) {
												submit();
											}
										}
									}}
									afterSubmit={() => {
										setSent(true);
										setAttachment([]);
										GAeventSubmit('Memorial-Form', 'Submit', 'Memorial-Form');
									}}
									customButtons={(item, formikBag) => (
										<div className="modal-button">
											<button
												type="submit"
												className="btn btn-primary btn-lg"
												disabled={loading}
											>
												Submit your memorial
											</button>
											{loading && <BounceLoader />}
											<button
												type="button"
												className="btn btn-primary btn-lg"
												onClick={(e) => openModal(e, formikBag.values)}
											>
												Preview your memorial
											</button>
										</div>
									)}
									edit={(formikBag) => (
										<>
											<h4>Pet information</h4>
											<div className="pet-mem-modal_form__form-pet-info">
												<CustomInput
													name="petName"
													placeholder="Pet's Name*"
													formikBag={formikBag}
												/>
												<CustomSelect
													name="species"
													placeholder="Species"
													onSelect={(value, option) => {
														formikBag.setFieldValue('species', value);
													}}
													onChange={(value, option) => {
														formikBag.setFieldValue('species', value);
													}}
													options={species}
													showSearch={false}
													onFocus={() => {}}
													transform
													formikBag={formikBag}
												/>

												<CustomDatePicker
													name="dateBirth"
													placeholder="Pet’s Birthday*"
													onChange={(value, dateString) => {
														formikBag.setFieldValue('dateBirth', value ? toStrictDate(value) : dateString);
													}}
													formikBag={formikBag}
												/>

												<CustomDatePicker
													name="datePass"
													placeholder="Pet’s Date of Passing*"
													onChange={(value, dateString) => {
														formikBag.setFieldValue('datePass', value ? toStrictDate(value) : dateString);
													}}
													formikBag={formikBag}
												/>

												<div className="vet-tes-modal__form-textarea">
													<CustomInput
														component="textarea"
														name="message"
														placeholder="Leave a message in memory of your pet*"
														formikBag={formikBag}
													/>
												</div>
												<div className="vet-tes-modal__form-textarea">
													<CustomInput
														component="textarea"
														name="story"
														placeholder="Tell us the ‘story’ of your pet*"
														formikBag={formikBag}
													/>
												</div>
											</div>
											<Field
												name="filePath"
												render={(field) => {
													return (
														<div className={field.form.errors.filePath && field.form.touched.filePath ? 'attachment attachment_cropped' : 'attachment'}>
															<h4 className="attachment-title">
																Upload pictures of your pet
															</h4>
															<AttachmentPreview
																attachment={attachment}
																setAttachment={setAttachment}
																formikBag={formikBag}
															/>
															<div className="attachment-upload-container">
																<FileUpload
																	update={(data) => onFileUpload(
																		data,
																		field,
																		formikBag,
																		'filePath',
																		maxFile,
																		imgExtension,
																		attachment,
																		maxImgCount,
																		setAttachment,
																	)}
																	type="jobInquiry"
																	fileType={FileType.File}
																	noRelation
																	asButton
																	buttonClassName="btn btn-secondary"
																	buttonCaption="Upload a file"
																	multiple={false}
																	aspectRatio={16 / 9}
																	maxFile={maxFile}
																	field={field}
																	fileNote=""
																	imgExtensions={imgExtension}
																/>
																<span className="attachment-note">
																	(Up to 6 pictures. Maximum file size is 10MB in
																	{` ${formatListWithOr(imgExtension)} `}
																	format)
																</span>
															</div>
															{
																field.form.errors.filePath && field.form.touched.filePath ? (
																	<div className="custom-input__error error_top-gap">{field.form.errors.filePath}</div>
																) : null
															}
														</div>
													);
												}}
											/>
											<h4>Personal information</h4>
											<div className="pet-mem-modal_form__form-personal-info">
												<CustomInput
													name="firstName"
													placeholder="First Name*"
													formikBag={formikBag}
												/>
												<CustomInput
													name="lastName"
													placeholder="Last Name"
													formikBag={formikBag}
												/>
												<CustomCheckbox
													isChecked={isChecked}
													setIsChecked={setIsChecked}
													formikBag={formikBag}
													capture="Would you like your name displayed with your memorial?"
												/>
												<CustomInput name="city" placeholder="City*" formikBag={formikBag} />
												<CustomSelect
													name="state"
													placeholder="State*"
													onSelect={(value, option) => {
														formikBag.setFieldValue('state', value);
													}}
													onChange={(value, option) => {
														formikBag.setFieldValue('state', value);
													}}
													onFocus={() => {}}
													options={stateListOptions}
													showSearch
													formikBag={formikBag}
												/>
												<CustomInput
													name="email"
													placeholder="Email Address*"
													formikBag={formikBag}
													trimValue
												/>
												<CustomSelect
													name="isAssist"
													placeholder="Did Lap of Love assist you?*"
													onSelect={(value, option) => {
														formikBag.setFieldValue('isAssist', value);
													}}
													onChange={(value, option) => {
														formikBag.setFieldValue('isAssist', value);
													}}
													options={assistOptions}
													showSearch={false}
													onFocus={() => {}}
													formikBag={formikBag}
												/>
												<FormikField
													fieldName="vetId"
													containerClassName="custom-field custom-field__search"
													render={(field) => (
														<CustomAutoComplete
															field={field}
															placeholder="Select or type Veterinarian Name"
															onChange={(value) => {
																formikBag.setFieldValue('vetId', value);
																getVeterinarians(value);
															}}
															onBlur={() => formikBag.setFieldTouched('vetId', true)}
															options={vetListOptions}
															adjustValue
														/>
													)}
												/>
												<div className="vet-tes-modal__form-textarea">
													<CustomInput
														component="textarea"
														name="vetTes"
														placeholder="Write a testimonial for your Lap of Love veterinarian"
														formikBag={formikBag}
													/>
												</div>
											</div>

											<div className="share">
												<h4>Would you like to share your memorial with friends or family?</h4>
												<CustomAutoTokenization
													name="share"
													placeholder="Email addresses of friends to share your memorial with"
													onChange={(value) => {
														formikBag.setFieldValue('share', value);
													}}
												/>
											</div>
											<FormikField
												containerClassName="captcha"
												fieldName="captcha"
												render={(fieldProps) => (
													<CustomReCAPTCHA
														formikBag={formikBag}
														onChange={(value) => {
															if (value) {
																formikBag.setFieldValue('captcha', value);
																setIsConfirmed(true);
															} else {
																formikBag.setFieldValue('captcha', '');
																setIsConfirmed(false);
															}
														}}
													/>
												)}
											/>
										</>
									)}
								/>
							</div>
						)}
					</div>
				</div>
			</Modal>
			{modalPreviewVisible && <PetMemorialPreviewModal modalVisible={modalPreviewVisible} setModalVisible={setModalPreviewVisible} memorial={formValues} />}
		</>
	);
};

export default PetMemorialModalForm;
